// src/components/forums/ReplyForm.tsx
import React, { useState } from 'react';
import MentionTextarea from '@/components/mentions/MentionTextarea';
import { Send, ImagePlus, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from "@/lib/utils";
import { useDropzone } from "react-dropzone";
import { useTheme } from "@/contexts/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import type { Member } from '@/types/user';

interface ReplyFormProps {
  onSubmit: (content: string, mentionedUsers: Member[], media?: File) => void;
  isSubmitting: boolean;
  initialContent?: string;
  buttonText?: string;
}

const ReplyForm: React.FC<ReplyFormProps> = ({
  onSubmit,
  isSubmitting,
  initialContent = '',
  buttonText = 'Post Reply'
}) => {
  const [content, setContent] = useState(initialContent);
  const [mentionedUsers, setMentionedUsers] = useState<Member[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isDarkMode } = useTheme();

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.webm', '.mov']
    },
    maxSize: 50 * 1024 * 1024, // 50MB max
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        console.log("ReplyForm: File selected:", file);
        setSelectedMedia(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setMediaPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0]?.message || 'Invalid file';
      setError(error);
      console.log("ReplyForm: File rejected:", error);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("ReplyForm: Form submitted");
    if (content.trim() || selectedMedia) {
      console.log("ReplyForm: Calling onSubmit with:", { content, mentionedUsers, selectedMedia });
      onSubmit(content, mentionedUsers, selectedMedia || undefined);
      setContent('');
      setMentionedUsers([]);
      setSelectedMedia(null);
      setMediaPreview(null);
    } else {
      console.log("ReplyForm: Content is empty and no media, not submitting");
      setError("Reply content cannot be empty");
    }
  };

  return (
    <form onSubmit={handleSubmit} {...getRootProps()}>
      <input {...getInputProps()} />
      <div
        className={cn(
        "rounded-xl border overflow-visible hover:border-gold-400 transition-all duration-300",
          isDarkMode ? "bg-dark-200 border-dark-300" : "bg-light-200 border-light-300"
        )}
      >
        <MentionTextarea
          value={content}
          onChange={(newContent, mentions) => {
            setContent(newContent);
            setMentionedUsers(mentions);
          }}
          placeholder="Write your reply here..."
          className={cn(
            "w-full p-4 bg-transparent border-none focus:outline-none resize-none min-h-[120px] placeholder:text-dark-800",
            isDarkMode ? "text-white" : "text-gray-900"
          )}
          disabled={isSubmitting}
        />

        {/* Media preview */}
        <AnimatePresence>
          {mediaPreview && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="relative px-4 pb-4"
            >
              {selectedMedia?.type.startsWith('video/') ? (
                <video 
                  src={mediaPreview} 
                  className="max-h-64 w-auto mx-auto rounded-lg" 
                  controls
                />
              ) : (
                <img 
                  src={mediaPreview} 
                  alt="Preview" 
                  className="max-h-64 w-auto mx-auto rounded-lg" 
                />
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMedia(null);
                  setMediaPreview(null);
                }}
                className="absolute top-2 right-6 p-1 rounded-full bg-dark-900/80 text-white hover:bg-dark-900"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="px-4 pb-2 text-red-500 text-sm">{error}</div>
        )}

        <div className="flex justify-between items-center p-4">
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            className={cn(
              "text-dark-800 hover:text-white hover:bg-dark-400",
              "transition-colors"
            )}
            disabled={isSubmitting}
          >
            <ImagePlus className="h-5 w-5" />
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting || (!content.trim() && !selectedMedia)}
            className="bg-gold-400 hover:bg-gold-500 text-dark-50 transition-colors"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <span>Posting...</span>
              </div>
            ) : (
              buttonText
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ReplyForm;